import * as React from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import {
  endOfMonth,
  endOfWeek,
  format,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subDays,
  subYears,
} from 'date-fns';
import useAuthorize from '../hooks/useAuthorize';
import { DASHBOARD_MODULE_ROLES } from '../roles/portalRoles';
import OrdersMonthlyTrendChart, { OrdersChartVolumeMetric } from './OrdersMonthlyTrendChart';
import { fetchOrdersValidation, OrdersValidationResponse } from './ordersValidationApi';

const LOOKBACK_YEARS = 3;

const formatInr = (value: number) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(
    value || 0
  );

const formatMt = (value: number) =>
  `${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(value || 0)} MT`;

const KpiCard: React.FC<{ label: string; value: string; loading: boolean }> = ({
  label,
  value,
  loading,
}) => (
  <Card variant="outlined">
    <CardContent>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      {loading ? <Skeleton width={120} height={36} /> : (
        <Typography variant="h5" sx={{ fontWeight: 600, mt: 0.5 }}>{value}</Typography>
      )}
    </CardContent>
  </Card>
);

const DashboardOrdersValidation = () => {
  useAuthorize(DASHBOARD_MODULE_ROLES);

  const getDefaultDateRange = () => {
    const now = new Date();
    return {
      fromDate: format(subYears(now, LOOKBACK_YEARS), 'yyyy-MM-dd'),
      toDate: format(now, 'yyyy-MM-dd'),
    };
  };

  const defaultDates = getDefaultDateRange();
  const [fromDate, setFromDate] = React.useState(defaultDates.fromDate);
  const [toDate, setToDate] = React.useState(defaultDates.toDate);
  const [quickRange, setQuickRange] = React.useState('last3Years');
  const [stateFilter, setStateFilter] = React.useState('');
  const [data, setData] = React.useState<OrdersValidationResponse | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [chartVolumeMetric, setChartVolumeMetric] = React.useState<OrdersChartVolumeMetric>('orders');

  const loadData = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await fetchOrdersValidation(fromDate, toDate, stateFilter || undefined));
    } catch (err: any) {
      setData(null);
      setError(err?.response?.data?.error || err.message || 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, stateFilter]);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  const handleQuickRangeChange = (event: any) => {
    const range = event.target.value;
    setQuickRange(range);
    if (!range) return;
    const now = new Date();
    let from = now;
    let to = now;
    switch (range) {
      case 'last7':
        from = subDays(now, 6);
        to = now;
        break;
      case 'thisMonth':
        from = startOfMonth(now);
        to = endOfMonth(now);
        break;
      case 'thisWeek':
        from = startOfWeek(now, { weekStartsOn: 1 });
        to = endOfWeek(now, { weekStartsOn: 1 });
        break;
      case 'ytd':
        from = startOfYear(now);
        to = now;
        break;
      case 'last3Years':
        from = subYears(now, LOOKBACK_YEARS);
        to = now;
        break;
      default:
        break;
    }
    setFromDate(format(from, 'yyyy-MM-dd'));
    setToDate(format(to, 'yyyy-MM-dd'));
  };

  const totals = data?.totals;
  const stateOptions = data?.state_options || [];

  return (
    <Box sx={{ p: 2, maxWidth: 1400, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <ShoppingCartIcon color="primary" />
        <Typography variant="h5" component="h1">Orders</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Live Credco order lines from the main database (completed orders). Use monthly totals and state
        splits to reconcile Delhi / Gujarat migration data against Tally.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Quick range</InputLabel>
                <Select value={quickRange} label="Quick range" onChange={handleQuickRangeChange}>
                  <MenuItem value="last3Years">Last 3 years</MenuItem>
                  <MenuItem value="ytd">Year to date</MenuItem>
                  <MenuItem value="thisMonth">This month</MenuItem>
                  <MenuItem value="thisWeek">This week</MenuItem>
                  <MenuItem value="last7">Last 7 days</MenuItem>
                  <MenuItem value="">Custom</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                label="From"
                type="date"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={fromDate}
                onChange={(e) => {
                  setFromDate(e.target.value);
                  setQuickRange('');
                }}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                label="To"
                type="date"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={toDate}
                onChange={(e) => {
                  setToDate(e.target.value);
                  setQuickRange('');
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>State / region</InputLabel>
                <Select
                  value={stateFilter}
                  label="State / region"
                  onChange={(e) => setStateFilter(e.target.value)}
                >
                  <MenuItem value="">All states</MenuItem>
                  {stateOptions.map((state) => (
                    <MenuItem key={state} value={state}>{state}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              {loading ? <CircularProgress size={28} /> : (
                <Typography variant="body2" color="text.secondary">
                  {totals?.order_count?.toLocaleString() || 0} orders
                  {totals?.min_order_date ? ` · ${totals.min_order_date} → ${totals.max_order_date}` : ''}
                </Typography>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <KpiCard label="Orders" value={(totals?.order_count || 0).toLocaleString()} loading={loading} />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <KpiCard label="Quantity" value={formatMt(totals?.qty_mt || 0)} loading={loading} />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <KpiCard label="Base (taxable)" value={formatInr(totals?.base_total || 0)} loading={loading} />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <KpiCard label="GST" value={formatInr(totals?.gst_total || 0)} loading={loading} />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <KpiCard label="Gross total" value={formatInr(totals?.gross_total || 0)} loading={loading} />
        </Grid>
      </Grid>

      <Card sx={{ mb: 2 }}>
        <CardHeader
          title="Monthly order intake"
          subheader={
            chartVolumeMetric === 'mt'
              ? 'Metric tons and gross sales (₹ Cr) by calendar month'
              : 'Order count and gross sales (₹ Cr) by calendar month'
          }
          action={
            <ToggleButtonGroup
              exclusive
              size="small"
              value={chartVolumeMetric}
              onChange={(_e, value: OrdersChartVolumeMetric | null) => {
                if (value) setChartVolumeMetric(value);
              }}
              aria-label="Chart volume metric"
            >
              <ToggleButton value="orders" aria-label="Order count">
                Orders
              </ToggleButton>
              <ToggleButton value="mt" aria-label="Metric tons">
                Metric tons
              </ToggleButton>
            </ToggleButtonGroup>
          }
        />
        <CardContent>
          {loading ? <Skeleton variant="rectangular" height={360} /> : (
            <OrdersMonthlyTrendChart
              data={data?.monthly || []}
              volumeMetric={chartVolumeMetric}
            />
          )}
        </CardContent>
      </Card>

      <Grid container spacing={2}>
        <Grid item xs={12} lg={6}>
          <Card>
            <CardHeader title="By state" subheader="Order state code, else customer address state" />
            <TableContainer sx={{ maxHeight: 420 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>State</TableCell>
                    <TableCell align="right">Orders</TableCell>
                    <TableCell align="right">MT</TableCell>
                    <TableCell align="right">Gross</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data?.by_state || []).map((row) => (
                    <TableRow key={row.state_label} hover>
                      <TableCell>{row.state_label}</TableCell>
                      <TableCell align="right">{row.order_count.toLocaleString()}</TableCell>
                      <TableCell align="right">{formatMt(row.qty_mt)}</TableCell>
                      <TableCell align="right">{formatInr(row.gross_total)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Card>
            <CardHeader title="Top customers" subheader="By gross order value in range" />
            <TableContainer sx={{ maxHeight: 420 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Customer</TableCell>
                    <TableCell>State</TableCell>
                    <TableCell align="right">Orders</TableCell>
                    <TableCell align="right">Gross</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data?.top_customers || []).map((row) => (
                    <TableRow key={row.customer_id} hover>
                      <TableCell>{row.customer_name}</TableCell>
                      <TableCell>{row.state_label}</TableCell>
                      <TableCell align="right">{row.order_count.toLocaleString()}</TableCell>
                      <TableCell align="right">{formatInr(row.gross_total)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="By polymer category" />
            <TableContainer sx={{ maxHeight: 320 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Category</TableCell>
                    <TableCell align="right">Orders</TableCell>
                    <TableCell align="right">MT</TableCell>
                    <TableCell align="right">Gross</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data?.by_category || []).map((row) => (
                    <TableRow key={row.category_name} hover>
                      <TableCell>{row.category_name}</TableCell>
                      <TableCell align="right">{row.order_count.toLocaleString()}</TableCell>
                      <TableCell align="right">{formatMt(row.qty_mt)}</TableCell>
                      <TableCell align="right">{formatInr(row.gross_total)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="By order status" />
            <TableContainer sx={{ maxHeight: 320 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Orders</TableCell>
                    <TableCell align="right">Gross</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data?.by_status || []).map((row) => (
                    <TableRow key={row.order_status_id} hover>
                      <TableCell>{row.status_label}</TableCell>
                      <TableCell align="right">{row.order_count.toLocaleString()}</TableCell>
                      <TableCell align="right">{formatInr(row.gross_total)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardHeader
              title="Monthly reconciliation table"
              subheader="Compare these rows to your Excel month-wise totals (orders, MT, base, GST, gross)"
            />
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Month</TableCell>
                    <TableCell align="right">Orders</TableCell>
                    <TableCell align="right">MT</TableCell>
                    <TableCell align="right">Base</TableCell>
                    <TableCell align="right">GST</TableCell>
                    <TableCell align="right">Gross</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data?.monthly || []).map((row) => (
                    <TableRow key={row.period}>
                      <TableCell>{row.period}</TableCell>
                      <TableCell align="right">{row.order_count.toLocaleString()}</TableCell>
                      <TableCell align="right">{row.qty_mt.toLocaleString('en-IN', { maximumFractionDigits: 4 })}</TableCell>
                      <TableCell align="right">{formatInr(row.base_total)}</TableCell>
                      <TableCell align="right">{formatInr(row.gst_total)}</TableCell>
                      <TableCell align="right">{formatInr(row.gross_total)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardOrdersValidation;
